import { Box, Slider } from '@material-ui/core';
import { LatLng, LatLngBounds, LatLngExpression, Map, Point } from 'leaflet';
import {
	ChangeEvent,
	createRef,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo
} from 'react';
import { Popup, useMap } from 'react-leaflet';
import produce from 'immer';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import DeleteIcon from '@material-ui/icons/Delete';
import { connect } from 'react-redux';
import { ColorPicker } from '..';
import {
	IMapDrawing,
	AppState,
	MapViewerPanel,
	projectRemoveDrawing,
	projectAddDrawing,
	IShape,
	AppThunkDispatch,
	ProjectActions,
	projectEditDrawingColor,
	projectEditDrawingPopup,
	projectEditDrawingWeight,
	projectCloseDrawingPopup
} from '../../../redux';
import { createNewDrawingId, isAppleDevice } from '../../../utils';
import { ShapeLayer, TooltipIconButton } from '../../atoms';
import { useCallbackSafeState } from '../../../hooks';

import './shape-popup-panel.styles.scss';
import { useTranslation } from 'react-i18next';

export interface IShapePopupPanelOwnProps {
	shapeLayer: ShapeLayer;
	proxyLayer?: L.Rectangle;
	drawing: IMapDrawing;
	onRemove?: () => void;
	createDuplicate: (offsetFunction: (coord: LatLng) => LatLng) => IShape;
	calculatePopupLocation?: () => LatLngExpression;
}

export interface IShapePopupPanelStateProps {
	disabled: boolean;
	removeDrawing: (drawing: IMapDrawing) => void;
	addDrawing: (drawing: IMapDrawing) => void;
	editDrawingColor: (drawing: IMapDrawing) => void;
	editDrawingPopup: (drawing: IMapDrawing) => void;
	editDrawingWeight: (drawing: IMapDrawing) => void;
	closeDrawingPopup: () => void;
}

function ShapePopupPanelView(
	props: IShapePopupPanelOwnProps & IShapePopupPanelStateProps
): ReactElement {
	const {
		disabled,
		shapeLayer,
		proxyLayer,
		drawing,
		removeDrawing,
		addDrawing,
		onRemove,
		createDuplicate,
		calculatePopupLocation,
		editDrawingColor,
		editDrawingPopup,
		closeDrawingPopup,
		editDrawingWeight
	} = props;

	const { t } = useTranslation('map');
	const duplicateShapeOffset: Point = new Point(30, 30);
	const popupContentRef: RefObject<HTMLDivElement> =
		createRef<HTMLDivElement>();
	const [getSafeDrawing, setSafeDrawing] =
		useCallbackSafeState<IMapDrawing>(drawing);
	const map: Map = useMap();

	const calculatePopupPosition = useCallback<
		(bounds: LatLngBounds) => LatLngExpression
	>(
		(bounds: LatLngBounds) => {
			if (calculatePopupLocation) {
				return calculatePopupLocation();
			}

			const center: LatLng = bounds.getCenter();
			const topLat: number = bounds.getNorthWest().lat;

			return [topLat, center.lng];
		},
		[shapeLayer]
	);

	const popupLayer: ShapeLayer = useMemo<ShapeLayer>(() => {
		return proxyLayer ? proxyLayer : shapeLayer;
	}, [shapeLayer, proxyLayer]);

	useEffect(() => {
		window.addEventListener('undo', onUndoRedo);
		window.addEventListener('redo', onUndoRedo);

		return () => {
			window.removeEventListener('undo', onUndoRedo);
			window.removeEventListener('redo', onUndoRedo);
		};
	}, []);

	useEffect(() => {
		setSafeDrawing(drawing);
	}, [drawing]);

	useEffect(() => {
		popupLayer.on('click', onShapeClicked);
		return () => {
			popupLayer.off('click', onShapeClicked);
		};
	}, [getSafeDrawing(), disabled]);

	useEffect(() => {
		evaluatePopupState();
	}, [getSafeDrawing().isPopupOpen, disabled]);

	useEffect(() => {
		setupShortcuts();

		return removeShortcuts;
	}, [getSafeDrawing().isPopupOpen]);

	function onUndoRedo(): void {
		shapeLayer.options.color = getSafeDrawing().shape.color;
		shapeLayer.options.weight = getSafeDrawing().shape.lineWidth;

		flushShapeChanges();
		endShapeEdit();
		closePopup();
	}

	function evaluatePopupState(): void {
		if (getSafeDrawing().isPopupOpen && !disabled) {
			openPopup();
			enableShapeEditing();
		}
		if (!getSafeDrawing().isPopupOpen || disabled) {
			closePopup();
			endShapeEdit();
		}
	}

	function onShapeClicked(): void {
		if (disabled) return;
		const newDrawing: IMapDrawing = produce(
			getSafeDrawing(),
			(draft: IMapDrawing) => {
				draft.isPopupOpen = true;
			}
		);
		editDrawingPopup(newDrawing);
	}

	function setupShortcuts(): void {
		document.addEventListener('keydown', handleKeyPress);
	}

	function removeShortcuts(): void {
		document.removeEventListener('keydown', handleKeyPress);
	}

	function handleKeyPress(event: KeyboardEvent): void {
		if (!getSafeDrawing().isPopupOpen) return;

		const actionDuplicate: boolean =
			(event.ctrlKey || (isAppleDevice() && event.metaKey)) &&
			event.key === 'd';
		const actionDelete: boolean = event.key === 'Delete';

		if (actionDuplicate) {
			duplicateDrawing();
			event.preventDefault();
		}

		if (actionDelete) {
			deleteDrawing();
			event.preventDefault();
		}
	}

	function enableShapeEditing(): void {
		if (!disabled) {
			setTimeout(() => {
				(popupLayer as any).editing.enable();
			}, 0);
		}
	}

	function onColorChange(color: string): void {
		const newDrawing: IMapDrawing = produce(
			getSafeDrawing(),
			(draft: IMapDrawing) => {
				draft.shape.color = color;
			}
		);
		editDrawingColor(newDrawing);

		shapeLayer.options.color = color;
		flushShapeChanges();
	}

	function onWeightChanged(
		_: ChangeEvent<{}>,
		value: number | number[]
	): void {
		const newDrawing: IMapDrawing = produce(
			getSafeDrawing(),
			(draft: IMapDrawing) => {
				draft.shape.lineWidth = value as number;
			}
		);

		editDrawingWeight(newDrawing);

		shapeLayer.options.weight = value as number;
		flushShapeChanges();
	}

	function deleteDrawing(): void {
		requestClose();
		if (onRemove) onRemove();
		removeDrawing(getSafeDrawing());
	}

	function duplicateDrawing(): void {
		const duplicateDrawing: IMapDrawing = {
			isPopupOpen: true,
			shape: createDuplicate(offsetPoint),
			id: createNewDrawingId()
		};
		requestClose();

		addDrawing(duplicateDrawing);
	}

	function offsetPoint(coord: LatLng): LatLng {
		const point: Point = map.project(coord);
		return map.unproject(point.add(duplicateShapeOffset));
	}

	function endShapeEdit(): void {
		(shapeLayer as any).editing.disable();
		(popupLayer as any).editing.disable();
	}

	function flushShapeChanges(): void {
		if (!proxyLayer) {
			(shapeLayer as any).editing.disable();
			(shapeLayer as any).editing.enable();
		} else {
			(shapeLayer as any).editing.enable();
			(shapeLayer as any).editing.disable();
		}
	}

	function requestClose(): void {
		closeDrawingPopup();
	}

	function closePopup(): void {
		setTimeout(() => popupLayer.closePopup(), 0);
	}

	function openPopup(): void {
		setTimeout(() => {
			if (!(popupLayer as any)._map) return;

			popupLayer.openPopup(
				calculatePopupPosition(popupLayer.getBounds())
			);
		}, 0);
	}

	return disabled && !getSafeDrawing().isPopupOpen ? (
		<></>
	) : (
		<Popup
			closeButton={false}
			className="shape-popup-panel"
			autoClose={false}
			onOpen={openPopup}
			onClose={requestClose}
		>
			<div
				ref={popupContentRef}
				className="shape-popup-panel-click-layer"
			>
				<TooltipIconButton
					onClick={() => {}}
					tooltip={t('actionButtons.edit.text.color')}
					placement="top"
					icon={
						<ColorPicker
							color={getSafeDrawing().shape.color}
							onChange={onColorChange}
						/>
					}
				/>
				<Box display="flex" width="100%" className="slider">
					<Slider
						className="slider"
						min={1}
						max={8}
						value={getSafeDrawing().shape.lineWidth}
						onChange={onWeightChanged}
					/>
				</Box>
				<TooltipIconButton
					onClick={duplicateDrawing}
					tooltip={t('actionButtons.edit.text.duplicate')}
					placement="top"
					icon={<FilterNoneIcon />}
				/>
				<TooltipIconButton
					onClick={deleteDrawing}
					tooltip={t('actionButtons.edit.text.delete')}
					placement="top"
					icon={<DeleteIcon htmlColor="#CF0101" />}
				/>
			</div>
		</Popup>
	);
}

function mapStateToProps(state: AppState, ownProps: IShapePopupPanelOwnProps) {
	return {
		...ownProps,
		disabled: !(
			state.mapViewer.panels.get(MapViewerPanel.Selection) || false
		)
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProjectActions>) {
	return {
		removeDrawing: (drawing: IMapDrawing) =>
			dispatch(projectRemoveDrawing(drawing)),
		addDrawing: (drawing: IMapDrawing) =>
			dispatch(projectAddDrawing(drawing)),
		editDrawingColor: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingColor(drawing)),
		editDrawingPopup: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingPopup(drawing)),
		closeDrawingPopup: () => dispatch(projectCloseDrawingPopup()),
		editDrawingWeight: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingWeight(drawing))
	};
}

export const ShapePopupPanel = connect(
	mapStateToProps,
	mapDispatchToProps
)(ShapePopupPanelView);
